import type { ParsedDemo, PlayerSlot, Round, Team } from '../schema';
import { tickAtFrame } from './selectors';

/**
 * Rounds won by each side *as the sides stand right now*. This is what a scoreboard shows: the
 * number beside `CT` belongs to whoever is playing CT at that moment, and it swaps with them at
 * halftime.
 */
export type SideScore = Record<Team, number>;

/**
 * A finished match's score, by **team** rather than by side.
 *
 * The demo carries no team name — `PlayerInfo` has a side, a SteamID and nothing that survives the
 * halftime swap — so the two teams are named by the side they started the match on. That is enough
 * to tell `13 : 7` from `7 : 13` and it never claims more than the data holds.
 */
export interface MatchScore {
  startedCt: number;
  startedT: number;
}

/**
 * One of the two teams, named by the side it opened the match on — the only name the demo lets us
 * give a team, and the same one `MatchScore` counts under.
 */
export type OpeningSide = 'ct' | 't';

function ctSlotsOf(round: Round): readonly PlayerSlot[] {
  return round.economy.filter((slot) => slot.team === 'CT').map((slot) => slot.slot);
}

/** The slots that opened the match on CT — the identity every later round is measured against. */
function openingCtSlots(rounds: readonly Round[]): ReadonlySet<PlayerSlot> {
  for (const round of rounds) {
    const opening = ctSlotsOf(round);

    if (opening.length > 0) return new Set(opening);
  }

  return new Set();
}

/**
 * Whether the opening CT team is on CT for this round, or `null` when the round says nothing —
 * an empty economy, or every slot without a sample at freeze-time end. A player who joined after
 * the opening round belongs to neither set, so the majority decides rather than the first slot.
 */
function openingCtOnCt(round: Round, opening: ReadonlySet<PlayerSlot>): boolean | null {
  if (opening.size === 0) return null;

  let familiar = 0;
  let strange = 0;

  for (const slot of ctSlotsOf(round)) {
    if (opening.has(slot)) familiar += 1;
    else strange += 1;
  }

  if (familiar === strange) return null;

  return familiar > strange;
}

/**
 * Which of the two teams won each round, in order.
 *
 * `Round.winner` is a *side*, and a side belongs to a different team in each half, so reading it
 * directly gives an answer that changes hands at halftime. Each round is attributed through the
 * side its own economy recorded — the only per-round side the schema carries — and a round the
 * mapping cannot be read from keeps the previous round's, which is what makes a match with no
 * economy data at all degrade to a side count rather than to nonsense.
 *
 * This is the one walk. `matchScore` counts it and the library card draws it, so the day the
 * attribution is corrected both are corrected — two walks of it is exactly what #141 was.
 */
export function roundWinners(demo: ParsedDemo): readonly OpeningSide[] {
  const { rounds } = demo.events;
  const opening = openingCtSlots(rounds);
  const winners: OpeningSide[] = [];
  let openingCtIsCt = true;

  for (const round of rounds) {
    openingCtIsCt = openingCtOnCt(round, opening) ?? openingCtIsCt;
    winners.push((round.winner === 'CT') === openingCtIsCt ? 'ct' : 't');
  }

  return winners;
}

/**
 * Which of the two teams each slot belongs to, indexed by slot.
 *
 * The first round that records a slot's side decides it, read through whether the opening CT team
 * was holding that side then — so a player who joined at halftime lands with the team they actually
 * played for rather than with whoever opened on the side they walked into. It is the same pair of
 * rules `roundWinners` attributes a round through, which is what keeps a scoreboard's rows and the
 * score above them from disagreeing about whose they are.
 */
export function openingSideBySlot(demo: ParsedDemo): readonly (OpeningSide | undefined)[] {
  const { rounds } = demo.events;
  const opening = openingCtSlots(rounds);
  const teams: (OpeningSide | undefined)[] = [];
  let openingCtIsCt = true;

  for (const round of rounds) {
    openingCtIsCt = openingCtOnCt(round, opening) ?? openingCtIsCt;

    for (const entry of round.economy) {
      if (entry.team === null || teams[entry.slot] !== undefined) continue;

      teams[entry.slot] = (entry.team === 'CT') === openingCtIsCt ? 'ct' : 't';
    }
  }

  return teams;
}

/**
 * A finished match's score by team, counted over `roundWinners`.
 *
 * `throughRound` bounds the count at a round index, which is the score *after* that round — what
 * §7.3's round list names on a hover.
 */
export function matchScore(demo: ParsedDemo, throughRound?: number): MatchScore {
  const score: MatchScore = { startedCt: 0, startedT: 0 };

  for (const [index, winner] of roundWinners(demo).entries()) {
    if (throughRound !== undefined && index > throughRound) break;

    if (winner === 'ct') score.startedCt += 1;
    else score.startedT += 1;
  }

  return score;
}

/**
 * The score a scoreboard shows at a sample position: rounds already decided, attributed to the
 * **team** that won them and then reported under the **side** that team is holding right now.
 *
 * Counting `Round.winner` by side is the bug this replaces (#141). It gives a pair of numbers that
 * is neither team's score once the halves swap — after a 12:3 first half it reads the second half's
 * CT wins onto the same `CT` number the other five players earned, so both numbers are sums over
 * two different sets of people.
 *
 * One walk does both jobs. A round that has *started* names the current sides even though it has
 * not been won by anyone yet; a round that has *ended* is also counted. That ordering is what makes
 * the score tick over at `endTick` — during the post-round the round it just decided is already on
 * the board, which is where a reader looks for it.
 */
export function sideScoreAtFrame(demo: ParsedDemo, frame: number): SideScore {
  const { rounds } = demo.events;
  const tick = tickAtFrame(demo.track, frame);
  const opening = openingCtSlots(rounds);
  const score: MatchScore = { startedCt: 0, startedT: 0 };
  let openingCtIsCt = true;

  for (const round of rounds) {
    if (round.startTick > tick) break;

    openingCtIsCt = openingCtOnCt(round, opening) ?? openingCtIsCt;

    if (round.endTick > tick) break;

    if ((round.winner === 'CT') === openingCtIsCt) score.startedCt += 1;
    else score.startedT += 1;
  }

  return openingCtIsCt
    ? { CT: score.startedCt, T: score.startedT }
    : { CT: score.startedT, T: score.startedCt };
}
